import { call, put, takeLatest } from 'redux-saga/effects';
import { SendApiRequest } from 'src/api';
import { apiRoutes } from 'src/api/routes';
import {
    fetchCalendarShifts,
    calendarShiftsFetched,
    calendarShiftsFetchFailed,
} from '../calendarShifts';
import { isHiddenClientEntity } from 'src/utils/shift';

// Page through a preset until it returns fewer than pageSize results, capped
// at MAX_PAGES so a runaway never blows past Atlas. Without paging, the
// hidden-client filter would silently chop the oldest past / latest future days.
const PAGE_SIZE = 1500;
const MAX_PAGES = 6;

function* fetchOnePreset(preset, baseParams) {
    const out = [];
    try {
        for (let page = 1; page <= MAX_PAGES; page++) {
            const response = yield call(SendApiRequest, {
                url: apiRoutes.jobs,
                method: 'GET',
                queryParams: {
                    ...baseParams,
                    pageSize: PAGE_SIZE,
                    page,
                    preset,
                },
            });
            const results = response.results || response.jobs || [];
            for (const r of results) {
                if (isHiddenClientEntity(r.entity)) continue;
                out.push({ ...r, __preset: preset });
            }
            if (results.length < PAGE_SIZE) break;
        }
    } catch (e) {
        // swallow; partial results are still useful
    }
    return out;
}

function* fetchCalendarShiftsEffect({ payload }) {
    try {
        const { rangeStart, rangeEnd, contractorId, clientId, province } =
            payload;

        // Send the exact epoch bounds the view already computed. The older
        // 'date-range' filter takes YYYY-MM-DD, which would mean formatting
        // these in some zone and having the server re-derive them in another -
        // the calendar anchors its day to the selected province, so a BC window
        // read back as Toronto lands three hours off.
        // pageSize is applied per page inside fetchOnePreset; this saga only
        // sets the query window and filters.
        const baseParams = {
            'shift-window': `${rangeStart},${rangeEnd}`,
        };
        if (contractorId) baseParams.staffer = contractorId;
        if (clientId) baseParams.entity = clientId;
        if (province && province.toLowerCase() !== 'all') {
            baseParams.province = province;
        }

        // One non-grouping preset for the whole window. The old pair split at
        // "now", and `upcoming_ongoing` collapses each recurring series to a
        // single card (jobs[0]) - correct for the list screens, but it made a
        // multi-day series render on one day only, so future dates went sparse
        // while past dates looked right.
        const shifts = yield call(fetchOnePreset, 'calendar_range', baseParams);

        yield put(calendarShiftsFetched({ shifts }));
    } catch (e) {
        yield put(calendarShiftsFetchFailed());
    }
}

export const calendarShiftsSagas = [
    takeLatest(fetchCalendarShifts.type, fetchCalendarShiftsEffect),
];
